<template>
  <li class="wc-order-type-cart-option">
    <WCImage
      :imageSrc="imageSrc"
      :imageAlt="orderType.name"
      defaultIcon="basket-shopping"
      class="wc-order-type-cart-option__icon"
    />

    <div class="wc-order-type-cart-option__info">
      <span class="wc-order-type-cart-option__name">
        {{ orderType.name }}
        <span v-if="isCurrent" class="wc-order-type-cart-option__current">{{ $t('current') }}</span>
        <span v-if="isNewCart" class="wc-order-type-cart-option__new">{{ $t('newCart') }}</span>
      </span>
      <!--
        TODO: Add order type description
      -->
    </div>

    <WCAddToCartQuantityAdjusterModernCard
      class="wc-order-type-cart-option__control"
      :item="item"
      :orderType="orderType"
      :variation="variation"
    >
      <template #atc-label>{{ $t('add') }}</template>
    </WCAddToCartQuantityAdjusterModernCard>
  </li>
</template>

<script setup lang="ts">
import { computed, defineProps, inject } from 'vue';
import WCImage from '@/components/WCImage/WCImage.vue';
import WCAddToCartQuantityAdjusterModernCard from '@/modules/cart/components/WCAddToCartQuantityAdjusterModernCard/WCAddToCartQuantityAdjusterModernCard.vue';
import useOrderTypes from '@/composables/useOrderTypes';
import { configurationKey } from '@/injections';
import { store } from '@/store';
import ItemSummary from '@/models/entities/items/item-summary';
import OrderType from '@/models/entities/cart/order-type';

const props = defineProps<{
  item: ItemSummary;
  orderType: OrderType;
  variation?: string | null;
}>();

const configuration = inject(configurationKey);
const { activeOrderType } = useOrderTypes();

const isCurrent = computed(() => props.orderType.id === activeOrderType.value?.id);

const imageSrc = computed(() => `${configuration?.cdn}/api/img/${props.orderType.image}`);

const nonEmptyCartOrderTypeIds = computed<string[]>(
  () =>
    store.getters['cart/nonEmptyCarts']?.map((cart: { orderType: string }) => cart.orderType) ?? [],
);

const isNewCart = computed(
  () => !isCurrent.value && !nonEmptyCartOrderTypeIds.value.includes(props.orderType.id),
);
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-order-type-cart-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid var(--dark-lighter-9);
}

@include media-breakpoint-up(md) {
  .wc-order-type-cart-option {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}

.wc-order-type-cart-option:last-of-type {
  border-bottom: none;
}

.wc-order-type-cart-option__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  overflow: hidden;
  object-fit: cover;
  background: var(--primary-lighter-9);
  color: var(--primary, $primary);
}

.wc-order-type-cart-option__info {
  flex: 1 1 auto;
  min-width: 0;
}

.wc-order-type-cart-option__name {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--dark, $dark);
}

.wc-order-type-cart-option__current {
  flex-shrink: 0;
  padding: 0.0625rem 0.375rem;
  border-radius: 0.25rem;
  background: var(--primary-lighter-9);
  color: var(--primary, $primary);
  font-size: 0.625rem;
  font-weight: 700;
  white-space: nowrap;
}

.wc-order-type-cart-option__new {
  flex-shrink: 0;
  padding: 0.0625rem 0.375rem;
  border-radius: 0.25rem;
  background: var(--warning-lighter-9, rgba($warning, 0.1));
  color: var(--warning-darker-3, get-color('warning', 'darker-3'));
  font-size: 0.625rem;
  font-weight: 700;
  white-space: nowrap;
}

.wc-order-type-cart-option__control {
  flex-shrink: 0;
  margin-left: auto;
}

.wc-order-type-cart-option__control.wc-atc-qty-adjuster-modern-card__atc {
  border: 1.5px solid var(--primary, $primary);
  border-radius: 1.375rem;
  background: none;
  color: var(--primary, $primary);
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  padding: 0.625rem 1.25rem;
  min-height: 2.5rem;
  height: auto;
  width: auto;
  white-space: nowrap;
  gap: 0;
}

.wc-order-type-cart-option__control.wc-atc-qty-adjuster-modern-card__atc :deep(.wc-icon) {
  display: none;
}

.wc-order-type-cart-option__control.wc-atc-qty-adjuster-modern-card {
  border: 1.5px solid var(--primary, $primary);
  border-radius: 1.375rem;
  height: 2.5rem;
  width: auto;
  gap: 0;
  padding: 0 0.25rem;
}

.wc-order-type-cart-option__control.wc-atc-qty-adjuster-modern-card
  :deep(.wc-atc-qty-adjuster-modern-card__minus),
.wc-order-type-cart-option__control.wc-atc-qty-adjuster-modern-card
  :deep(.wc-atc-qty-adjuster-modern-card__plus) {
  align-self: auto;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
}

.wc-order-type-cart-option__control.wc-atc-qty-adjuster-modern-card
  :deep(.wc-atc-qty-adjuster-modern-card__qty),
.wc-order-type-cart-option__control.wc-atc-qty-adjuster-modern-card
  :deep(.wc-atc-qty-adjuster-modern-card__qty-input) {
  align-self: stretch;
  height: auto;
  border: none;
  border-left: 1px solid var(--dark-lighter-5);
  border-right: 1px solid var(--dark-lighter-5);
  border-radius: 0;
  background: none;
  font-size: 0.8125rem;
  padding: 0 0.25rem;
  width: 1.75rem;
  min-width: 1.75rem;
  max-width: 1.75rem;
}

.wc-order-type-cart-option__control.wc-atc-qty-adjuster-modern-card
  :deep(.wc-atc-qty-adjuster-modern-card__qty-input) {
  field-sizing: fixed;
}

.wc-order-type-cart-option__control.wc-atc-qty-adjuster-modern-card
  :deep(.wc-atc-qty-adjuster-modern-card__qty-input:focus) {
  border-color: var(--dark-lighter-5);
  box-shadow: none;
  outline: none;
}
</style>
